import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Typography, Button as MuiButton } from '@mui/material';
import { useForm } from 'react-hook-form';
import { UploadOutlined } from '@ant-design/icons';

import validators from 'utils/validators';
import FormCard from 'components/FormCard';
import AnimateButton from 'components/@extended/AnimateButton';
import Button from 'components/Button';
import Loader from 'components/Loader';
import React, { useEffect, useRef, useState } from 'react';
import DadosDaCredencial from './DadosDaCredencial';
import * as actions from 'reducers/credenciais.reducer';
import MainCard from 'components/MainCard';
import MainLayout from 'layout/MainLayout/index';

import {
  actions as routeActions,
  types as routes
} from 'reducers/rotas.actions';

const CredencialPage = () => {
  const dispatch = useDispatch();
  const credencial = useSelector((state) => state.credenciais.single.data);
  const loading = useSelector((state) => state.credenciais.single.loading);
  const isEditMode = useSelector((state) => state.credenciais.single.isEditMode);
  const { banks: banksDomain } = useSelector((state) => state.credenciais.domain);

  const [privateKeyFile, setPrivateKeyFile] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const privateKeyRef = useRef(null);
  const certificateRef = useRef(null);

  const rules = {
    tipo: validators.object({ required: true }),
    descricao: validators.string({ required: true }),
    cpfCnpj: validators.string({ required: false }),
    padrao: {},
    dataExpiracao: validators.date({ required: false }),
    clientId: validators.string({ required: false }),
    clientSecret: validators.string({ required: false }),
    apiId: validators.string({ required: false }),
    login: validators.string({ required: false })
  };

  const initialValues = {
    tipo: null,
    descricao: '',
    cpfCnpj: '',
    padrao: false,
    dataExpiracao: '',
    clientId: '',
    clientSecret: '',
    apiId: '',
    login: ''
  };

  const formProps = {
    ...useForm({ defaultValues: initialValues }),
    rules,
    initialValues
  };

  const formatDateToBR = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  useEffect(() => {
    if (credencial) {
      formProps.reset({
        ...credencial,
        tipo: banksDomain.find((b) => b.id === credencial.tipo) ?? null,
        cpfCnpj: credencial.cpfCnpj ?? '',
        padrao: credencial.default ?? false,
        dataExpiracao: formatDateToBR(credencial.dataExpiracao),
        clientId: credencial.clientId ?? '',
        clientSecret: credencial.clientSecret ?? '',
        apiId: credencial.apiId ?? '',
        login: credencial.login ?? ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credencial]);

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      tipo: values.tipo?.id,
      default: values.padrao ?? false
    };
    delete payload.padrao;

    if (isEditMode) {
      dispatch(actions.editarCredencialRequest({ value: { ...payload, id: credencial.id }, privateKeyFile, certificateFile }));
    } else {
      dispatch(actions.criarCredencialRequest({ value: payload, privateKeyFile, certificateFile }));
    }
  };

  const handleGoBack = () => {
    dispatch(routeActions.redirectTo(routes.CREDENCIAIS));
  };

  if (loading) {
    return (
      <MainLayout>
        <Loader />
      </MainLayout>
    );
  }

  if (isEditMode && !credencial?.id) {
    return (
      <MainLayout>
        <MainCard border={false} title="Credencial não encontrada">
          <Grid container style={{ marginTop: 20, justifyContent: 'end', marginBottom: 20 }} spacing={2}>
            <Grid item xs={12} md={4}>
              <AnimateButton>
                <Button
                  disableElevation
                  fullWidth
                  size="large"
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={handleGoBack}
                >
                  VOLTAR
                </Button>
              </AnimateButton>
            </Grid>
          </Grid>
        </MainCard>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <form noValidate onSubmit={formProps.handleSubmit(handleSubmit)}>
        <MainCard
          contentSX={{ paddingLeft: 1, paddingRight: 1 }}
          border={false}
          title={
            !isEditMode ? (
              'Nova Credencial de Integração'
            ) : (
              <Box>
                <Typography variant="h2">{credencial?.descricao}</Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Credencial de Integração
                </Typography>
              </Box>
            )
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormCard title="DADOS DA CREDENCIAL">
              <DadosDaCredencial formProps={formProps} banksDomain={banksDomain} />
            </FormCard>

            <FormCard title="CERTIFICADOS">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Chave Privada (privateKey)
                  </Typography>
                  <input
                    type="file"
                    ref={privateKeyRef}
                    style={{ display: 'none' }}
                    onChange={(e) => setPrivateKeyFile(e.target.files[0] || null)}
                  />
                  <MuiButton
                    variant="outlined"
                    startIcon={<UploadOutlined />}
                    onClick={() => privateKeyRef.current?.click()}
                  >
                    {privateKeyFile ? privateKeyFile.name : 'Selecionar arquivo'}
                  </MuiButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Certificado (certificate)
                  </Typography>
                  <input
                    type="file"
                    ref={certificateRef}
                    style={{ display: 'none' }}
                    onChange={(e) => setCertificateFile(e.target.files[0] || null)}
                  />
                  <MuiButton
                    variant="outlined"
                    startIcon={<UploadOutlined />}
                    onClick={() => certificateRef.current?.click()}
                  >
                    {certificateFile ? certificateFile.name : 'Selecionar arquivo'}
                  </MuiButton>
                </Grid>
              </Grid>
            </FormCard>
          </Box>
        </MainCard>

        <Grid container style={{ marginTop: 20, justifyContent: 'end', marginBottom: 20 }} spacing={2}>
          <Grid item xs={12} md={4}>
            <AnimateButton>
              <Button
                disableElevation
                fullWidth
                size="large"
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleGoBack}
              >
                VOLTAR
              </Button>
            </AnimateButton>
          </Grid>
          <Grid item xs={12} md={4}>
            <AnimateButton>
              <Button disableElevation fullWidth size="large" variant="contained" color="primary" type="submit">
                SALVAR
              </Button>
            </AnimateButton>
          </Grid>
        </Grid>
      </form>
    </MainLayout>
  );
};

export default CredencialPage;
